import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Download, Search, ShoppingBag, MapPin, CheckCircle2, AlertTriangle, Eye, ArrowRightCircle, Link2, XCircle, Ban, Timer, Receipt } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatINR } from '@/lib/format';
import { listItems } from '@/api/items';
import {
  getShopifyOrders, getShopifyOrder, pullShopifyOrders,
  processShopifyOrder, resolveShopifySku, cancelShopifyOrder, holdShopifyOrder, unholdShopifyOrder, retryShopifyPickList,
  type ShopifyOrderRow, type StockShort,
} from '@/api/shopify';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';

/** Payment column: COD orders say so (with the cash to collect); others show Shopify's payment status. */
function paymentBadge(o: { financialStatus: string | null; paymentMethod?: string; codAmount?: number | null }) {
  const fs = o.financialStatus ?? '';
  if (o.paymentMethod === 'COD' && fs !== 'VOIDED' && fs !== 'REFUNDED') {
    const label = fs === 'PARTIALLY_PAID' ? 'Part-paid COD' : 'COD';
    const due = o.codAmount != null ? ` · collect ₹${formatINR(o.codAmount)}` : '';
    return <span title={`Cash on Delivery${due}`}><Badge tone="accent">{label}</Badge></span>;
  }
  return <Badge tone={fs === 'PAID' ? 'success' : fs === 'VOIDED' || fs === 'REFUNDED' ? 'muted' : 'warning'}>{fs || '—'}</Badge>;
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'info' | 'muted' | 'danger'> = {
  received: 'info', processing: 'warning', done: 'success', ignored: 'muted', cancelled: 'danger',
};

/**
 * Cancel-from-ERP (the token has `write_orders`). The backend still refuses it while
 * Configurations → Shopify → Order write-back is off.
 */
const CANCEL_ENABLED = true;

/** Date + time in local IN format (list/detail). */
function dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function OnlineOrdersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const docViewer = useDocViewer();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [mapSku, setMapSku] = useState<string | null>(null); // SKU currently being mapped
  const [cancelId, setCancelId] = useState<number | null>(null); // order being cancelled
  const [holdId, setHoldId] = useState<number | null>(null); // order being put on hold
  const [holdReason, setHoldReason] = useState('');

  const period = usePeriod();
  const { range } = period;
  const filter = useMemo(() => ({ status, search, from: range?.from, to: range?.to }), [status, search, range]);
  const [exporting, setExporting] = useState(false);

  // Every order matching the current filters — no 500 cap.
  async function onExport() {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.set('status', status);
    if (search.trim()) params.set('search', search.trim());
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    const qs = params.toString();
    const name = `GRHAMOY_Online_Orders${status && status !== 'all' ? `_${status}` : ''}.xlsx`;
    setExporting(true);
    try {
      await downloadExcel(`/shopify/orders/export.xlsx${qs ? `?${qs}` : ''}`, name, 180000);
    } catch (err) {
      toast.push(apiError(err, 'Export failed'), 'error');
    } finally {
      setExporting(false);
    }
  }
  const query = useQuery({ queryKey: ['shopify-orders', filter], queryFn: () => getShopifyOrders(filter) });
  const rows = query.data ?? [];

  const detail = useQuery({
    queryKey: ['shopify-order', openId],
    queryFn: () => getShopifyOrder(openId as number),
    enabled: openId != null,
  });

  const pull = useMutation({
    mutationFn: pullShopifyOrders,
    onSuccess: (r) => {
      if (r.startPointSet) {
        toast.push(`Start point initialised to ${new Date(r.startPointSet).toLocaleString()} — new orders from now on will be pulled.`, 'info');
      } else {
        toast.push(r.pulled ? `Pulled ${r.pulled} new order(s)` : 'No new orders since last pull', r.pulled ? 'success' : 'info');
      }
      qc.invalidateQueries({ queryKey: ['shopify-orders'] });
    },
    onError: (e) => toast.push(apiError(e, 'Pull failed'), 'error'),
  });

  const process = useMutation({
    mutationFn: (id: number) => processShopifyOrder(id),
    onSuccess: (r, id) => {
      if (r.pickListCreated) toast.push(`Processed to ERP order ${r.enqNo} — sent to Pick List`, 'success');
      else toast.push(`Processed to ERP order ${r.enqNo} — waiting for stock: ${shortText(r.waitingFor)}. It goes to the Pick List automatically once stock arrives.`, 'warning');
      // The order's receipt (Shopify tax-invoice layout) opens as soon as it reaches the ERP.
      docViewer.open('online-receipt', id, rows.find((x) => x.id === id)?.orderName ?? d?.orderName ?? undefined);
      qc.invalidateQueries({ queryKey: ['shopify-orders'] });
      qc.invalidateQueries({ queryKey: ['shopify-order', openId] });
    },
    onError: (e) => toast.push(apiError(e, 'Process failed'), 'error'),
  });

  // Waiting for stock → try the Pick List again now (the server also retries every 10 minutes).
  const retry = useMutation({
    mutationFn: (id: number) => retryShopifyPickList(id),
    onSuccess: (r) => {
      if (r.pickListCreated) toast.push(`${r.enqNo} sent to Pick List`, 'success');
      else toast.push(`Still waiting for stock: ${shortText(r.waitingFor) || r.reason}`, 'warning');
      qc.invalidateQueries({ queryKey: ['shopify-orders'] });
      qc.invalidateQueries({ queryKey: ['shopify-order', openId] });
    },
    onError: (e) => toast.push(apiError(e, 'Retry failed'), 'error'),
  });

  const cancel = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Parameters<typeof cancelShopifyOrder>[1] }) => cancelShopifyOrder(id, payload),
    onSuccess: () => {
      toast.push('Order cancelled — Shopify updated and any reserved stock released', 'success');
      setCancelId(null);
      qc.invalidateQueries({ queryKey: ['shopify-orders'] });
      qc.invalidateQueries({ queryKey: ['shopify-order', openId] });
    },
    onError: (e) => toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['shopify-orders'] });
    qc.invalidateQueries({ queryKey: ['shopify-order', openId] });
  };
  const hold = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => holdShopifyOrder(id, reason),
    onSuccess: () => { toast.push('Order put on hold — it can’t be processed until released', 'success'); setHoldId(null); setHoldReason(''); invalidate(); },
    onError: (e) => toast.push(apiError(e, 'Hold failed'), 'error'),
  });
  const unhold = useMutation({
    mutationFn: (id: number) => unholdShopifyOrder(id),
    onSuccess: () => { toast.push('Hold released', 'success'); invalidate(); },
    onError: (e) => toast.push(apiError(e, 'Unhold failed'), 'error'),
  });

  const resolveSku = useMutation({
    mutationFn: ({ sku, itemId }: { sku: string; itemId: number }) => resolveShopifySku(sku, itemId),
    onSuccess: (r) => {
      toast.push(`Mapped ${r.sku} → ${r.itemCode} (${r.linesBackfilled} line(s) updated)`, 'success');
      if (r.processedWithOtherItem?.length) toast.push(`Already processed with a different item — fix in the ERP order: ${r.processedWithOtherItem.join(', ')}`, 'warning');
      setMapSku(null);
      qc.invalidateQueries({ queryKey: ['shopify-order', openId] });
      qc.invalidateQueries({ queryKey: ['shopify-orders'] });
    },
    onError: (e) => toast.push(apiError(e, 'Mapping failed'), 'error'),
  });

  const d = detail.data;
  const allMapped = !!d && d.lines.length > 0 && d.lines.every((l) => l.mapped);
  const alreadyProcessed = !!d?.erpEnquiryId;
  const isCancelled = d?.erpStatus === 'cancelled' || !!d?.cancelledAt;
  const isHeld = !!d?.heldAt;

  return (
    <div>
      <SectionHeader
        title="Online Orders"
        subtitle={range ? `Orders placed ${range.label}` : undefined}
      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full" title="Search by order #, name, email or phone">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search online orders"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="received">Received</option>
            <option value="processing">Processing</option>
            <option value="done">Done</option>
            <option value="held">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <div className="col-span-3 flex gap-1.5 sm:col-span-1 sm:ml-auto sm:self-end [&>button]:flex-1 sm:[&>button]:flex-none">
          <button
            type="button"
            disabled={exporting}
            onClick={onExport}
            title="Download every order matching these filters (no limit) — orders plus their line items"
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-primary)] shadow-sm transition hover:bg-[var(--bg-secondary)] disabled:opacity-60 sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            <Download size={13} /> {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            disabled={pull.isPending}
            onClick={() => pull.mutate()}
            title="Pull new orders from Shopify now. Auto-pull also runs every 10 min when Order Sync is enabled in Shopify → Settings (the pull start point is set there too)."
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60 sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            <RefreshCw size={13} className={pull.isPending ? 'animate-spin' : ''} /> {pull.isPending ? 'Pulling…' : 'Pull Orders'}
          </button>
        </div>
      </FilterBar>

      <Table<ShopifyOrderRow>
        paginated
        storageKey="shopify-orders"
        title="Shopify Orders"
        count={`${rows.length}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'order', header: 'Order', cell: (r) => <span className="font-extrabold text-accent">{r.orderName || r.shopifyOrderId}</span> },
          { key: 'date', header: 'Placed', cell: (r) => dt(r.shopifyCreatedAt) },
          { key: 'customer', header: 'Customer', cell: (r) => <span className="font-bold">{r.customerName || '—'}</span> },
          { key: 'city', header: 'City', cell: (r) => r.shipCity || '—' },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.lineCount },
          { key: 'total', header: 'Total', align: 'right', cell: (r) => `₹${formatINR(Number(r.totalPrice ?? 0))}` },
          { key: 'pay', header: 'Payment', align: 'center', cell: (r) => paymentBadge(r) },
          { key: 'status', header: 'Status', align: 'center', cell: (r) => r.heldAt
            ? <span title={r.holdReason ?? 'On hold'}><Badge tone="warning">on hold</Badge></span>
            : r.waitingForStock
              ? <span title={`Waiting for stock: ${shortText(r.waitingFor)}`}><Badge tone="danger">waiting for stock</Badge></span>
              : <Badge tone={STATUS_TONE[r.erpStatus] ?? 'muted'}>{r.erpStatus}</Badge> },
          { key: 'actions', header: '', align: 'right', alwaysVisible: true, cell: (r) => (
            <div className="flex items-center justify-end gap-1">
              <Button variant="glass" size="sm" icon={<Eye size={13} />} onClick={() => setOpenId(r.id)}>View</Button>
              {r.waitingForStock && !r.heldAt && (
                <Button variant="glass" size="sm" disabled={retry.isPending} onClick={() => retry.mutate(r.id)} title={`Waiting for stock: ${shortText(r.waitingFor)}`}>Retry</Button>
              )}
              {(r.erpStatus === 'processing' || r.erpStatus === 'done') && (
                <Button variant="glass" size="sm" icon={<Receipt size={13} />} onClick={() => docViewer.open('online-receipt', r.id, r.orderName ?? undefined)} title="Open the order receipt">Receipt</Button>
              )}
              {r.heldAt ? (
                <Button variant="glass" size="sm" disabled={unhold.isPending} onClick={() => unhold.mutate(r.id)}>Unhold</Button>
              ) : (r.erpStatus === 'received' && !r.cancelledAt) ? (
                <Button variant="glass" size="sm" icon={<Timer size={13} />} onClick={() => setHoldId(r.id)}>Hold</Button>
              ) : null}
            </div>
          ) },
        ]}
      />

      <Modal open={openId != null} onClose={() => setOpenId(null)} size="2xl"
        title={<span className="flex items-center gap-2"><ShoppingBag size={16} /> {d?.orderName || 'Order'}</span>}
        footer={d ? (
          isCancelled ? (
            <div className="flex w-full items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-danger"><Ban size={16} /> {d.cancelledAt ? 'Cancelled in Shopify' : 'Order cancelled'}{d.cancelReason ? ` · ${d.cancelReason}` : ''} — cannot be fulfilled</span>
              <Button variant="glass" onClick={() => setOpenId(null)}>Close</Button>
            </div>
          ) : isHeld ? (
            <div className="flex w-full items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-[var(--warning,#b45309)]"><Timer size={16} /> On hold{d.holdReason ? ` · ${d.holdReason}` : ''} — release to process</span>
              <div className="flex items-center gap-2">
                <Button variant="primary" disabled={unhold.isPending} onClick={() => unhold.mutate(d.id)}>{unhold.isPending ? 'Releasing…' : 'Release hold'}</Button>
                <Button variant="glass" onClick={() => setOpenId(null)}>Close</Button>
              </div>
            </div>
          ) : alreadyProcessed ? (
            <div className="flex w-full items-center justify-between gap-3 text-sm">
              {d.erpEnqStage === 'new' ? (
                // Processed, but short of stock — it is NOT in Order Review; it goes to the Pick List on its own.
                <span className="flex min-w-0 items-start gap-2 text-[var(--warning,#b45309)]">
                  <Timer size={16} className="mt-0.5 shrink-0" />
                  <span>ERP order <strong>{d.erpEnqNo}</strong> · <strong>waiting for stock</strong>{d.waitingFor?.length ? ` — ${shortText(d.waitingFor)}` : ''}. Goes to the Pick List automatically once stock arrives.</span>
                </span>
              ) : (
                <span className="flex items-center gap-2 text-success"><CheckCircle2 size={16} /> Processed to ERP order <strong>{d.erpEnqNo}</strong> · on the Pick List / later</span>
              )}
              <div className="flex shrink-0 items-center gap-2">
                {d.erpEnqStage === 'new' && (
                  <Button variant="primary" disabled={retry.isPending} onClick={() => retry.mutate(d.id)}>{retry.isPending ? 'Checking…' : 'Retry now'}</Button>
                )}
                {CANCEL_ENABLED && <Button variant="danger" icon={<XCircle size={15} />} onClick={() => setCancelId(d.id)}>Cancel Order</Button>}
                <Button variant="glass" icon={<Receipt size={15} />} onClick={() => docViewer.open('online-receipt', d.id, d.orderName ?? undefined)}>Receipt</Button>
                <Button variant="glass" onClick={() => setOpenId(null)}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="flex w-full items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {CANCEL_ENABLED && <Button variant="danger" icon={<XCircle size={15} />} onClick={() => setCancelId(d.id)}>Cancel Order</Button>}
                <Button variant="glass" icon={<Timer size={15} />} onClick={() => setHoldId(d.id)}>Hold</Button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[var(--text-muted)]">
                  {allMapped ? 'All lines mapped — ready to create the ERP order.' : 'Map every line to an ERP item before processing.'}
                </span>
                <Button variant="primary" icon={<ArrowRightCircle size={15} />} disabled={!allMapped || process.isPending}
                  onClick={() => process.mutate(d.id)}>
                  {process.isPending ? 'Processing…' : 'Process to ERP'}
                </Button>
              </div>
            </div>
          )
        ) : undefined}>
        {detail.isFetching && !d ? (
          <div className="py-10 text-center text-[var(--text-muted)]">Loading…</div>
        ) : d ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Placed" value={dt(d.shopifyCreatedAt)} />
              <Field label="Payment" value={d.paymentMethod === 'COD' ? `Cash on Delivery${d.codAmount != null ? ` · collect ₹${formatINR(d.codAmount)}` : ''} (${d.financialStatus || '—'})` : d.financialStatus || '—'} />
              <Field label="Fulfilment" value={d.fulfillmentStatus || '—'} />
              <Field label="ERP Status" value={d.erpStatus} />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Customer</div>
                <div className="text-sm font-semibold">{d.customerName || '—'}</div>
                <div className="text-xs text-[var(--text-muted)]">{d.customerEmail || ''}</div>
                <div className="text-xs text-[var(--text-muted)]">{d.customerPhone || ''}</div>
              </div>
              <div className="rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3">
                <div className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]"><MapPin size={12} /> Ship To</div>
                <div className="text-sm font-semibold">{d.shipName || '—'}</div>
                <div className="text-xs text-[var(--text-muted)]">
                  {[d.shipAddress1, d.shipAddress2, d.shipCity, d.shipProvince, d.shipZip, d.shipCountry].filter(Boolean).join(', ') || '—'}
                </div>
                {d.shipPhone && <div className="text-xs text-[var(--text-muted)]">{d.shipPhone}</div>}
              </div>
            </div>

            <Table<typeof d.lines[number]>
              storageKey="shopify-order-lines"
              title="Line Items"
              count={`${d.lines.length}`}
              rows={d.lines}
              rowKey={(l) => l.id}
              columns={[
                { key: 'sku', header: 'SKU', cell: (l) => l.sku || '—' },
                { key: 'title', header: 'Product', cell: (l) => `${l.title || ''}${l.variantTitle ? ` — ${l.variantTitle}` : ''}` },
                { key: 'qty', header: 'Qty', align: 'right', cell: (l) => <strong>{l.quantity}</strong> },
                { key: 'price', header: 'Price', align: 'right', cell: (l) => `₹${formatINR(Number(l.price ?? 0))}` },
                {
                  key: 'erp', header: 'ERP Item', align: 'right', cell: (l) => (l.mapped
                    ? <span className="flex items-center justify-end gap-1 text-success"><CheckCircle2 size={13} /> {l.itemCode}</span>
                    : (
                      <div className="flex items-center justify-end gap-2">
                        <span className="flex items-center gap-1 text-warning"><AlertTriangle size={13} /> Unmapped</span>
                        {!alreadyProcessed && l.sku && (
                          <Button variant="glass" size="sm" icon={<Link2 size={12} />} onClick={() => setMapSku(l.sku)}>Map</Button>
                        )}
                      </div>
                    )),
                },
              ]}
            />

            <div className="flex flex-wrap justify-end gap-6 text-sm">
              <span>Subtotal: <strong>₹{formatINR(Number(d.subtotal ?? 0))}</strong></span>
              <span>Tax: <strong>₹{formatINR(Number(d.totalTax ?? 0))}</strong></span>
              <span>Discount: <strong>₹{formatINR(Number(d.totalDiscount ?? 0))}</strong></span>
              <span className="text-base">Total: <strong>₹{formatINR(Number(d.totalPrice ?? 0))}</strong></span>
            </div>
            {d.note && <div className="rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 text-xs"><strong>Note:</strong> {d.note}</div>}
          </div>
        ) : (
          <div className="py-10 text-center text-[var(--text-muted)]">No data</div>
        )}
      </Modal>

      <MapSkuModal
        sku={mapSku}
        onClose={() => setMapSku(null)}
        onPick={(itemId) => mapSku && resolveSku.mutate({ sku: mapSku, itemId })}
        pending={resolveSku.isPending}
      />

      <CancelOrderModal
        orderName={cancelId != null ? (d?.orderName || rows.find((r) => r.id === cancelId)?.orderName || '') : ''}
        open={cancelId != null}
        onClose={() => setCancelId(null)}
        onConfirm={(payload) => cancelId != null && cancel.mutate({ id: cancelId, payload })}
        pending={cancel.isPending}
      />

      <Modal
        open={holdId != null}
        onClose={() => { setHoldId(null); setHoldReason(''); }}
        size="sm"
        title={<span className="flex items-center gap-2"><Timer size={16} /> Hold order {rows.find((r) => r.id === holdId)?.orderName ?? ''}</span>}
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button variant="glass" onClick={() => { setHoldId(null); setHoldReason(''); }}>Cancel</Button>
            <Button variant="primary" disabled={!holdReason.trim() || hold.isPending}
              onClick={() => holdId != null && hold.mutate({ id: holdId, reason: holdReason.trim() })}>
              {hold.isPending ? 'Holding…' : 'Put on hold'}
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          <p className="text-xs text-[var(--text-muted)]">
            While on hold, this order can’t be processed into the ERP — use it when the customer hasn’t confirmed. A reason is required.
          </p>
          <label className="block text-xs font-semibold text-[var(--text-secondary)]">Reason for hold</label>
          <textarea
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            rows={3}
            autoFocus
            placeholder="e.g. Awaiting customer confirmation on call"
            className="w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm outline-none focus:border-accent"
          />
        </div>
      </Modal>
    </div>
  );
}

const CANCEL_REASONS = [
  { value: 'CUSTOMER', label: 'Customer changed / cancelled their mind' },
  { value: 'INVENTORY', label: 'Item out of stock' },
  { value: 'FRAUD', label: 'Fraudulent order' },
  { value: 'DECLINED', label: 'Payment declined' },
  { value: 'OTHER', label: 'Other' },
];

/** Confirm cancelling a Shopify order from the ERP (also cancels on Shopify). */
function CancelOrderModal({
  orderName, open, onClose, onConfirm, pending,
}: {
  orderName: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: { reason: string; note: string; refund: boolean; restock: boolean; notifyCustomer: boolean }) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState('CUSTOMER');
  const [note, setNote] = useState('');
  const [refund, setRefund] = useState(true);
  const [restock, setRestock] = useState(true);
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  return (
    <Modal open={open} onClose={onClose} size="md"
      title={<span className="flex items-center gap-2 text-danger"><XCircle size={16} /> Cancel order {orderName}</span>}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="glass" onClick={onClose} disabled={pending}>Keep Order</Button>
          <Button variant="danger" icon={<Ban size={15} />} disabled={pending}
            onClick={() => onConfirm({ reason, note, refund, restock, notifyCustomer })}>
            {pending ? 'Cancelling…' : 'Cancel Order'}
          </Button>
        </div>
      }>
      <div className="space-y-4">
        <p className="text-sm text-[var(--text-muted)]">
          This cancels the order on <strong>Shopify</strong> and, if it was processed to the ERP, releases any reserved
          stock and marks the ERP enquiry cancelled. Dispatched orders cannot be cancelled here.
        </p>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Reason</label>
          <Select value={reason} onChange={(e) => setReason(e.target.value)}>
            {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Note (added to the Shopify order)</label>
          <Input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Customer called, wants a different colour" />
        </div>
        <div className="space-y-2">
          <CheckRow checked={refund} onChange={setRefund} label="Refund the customer" hint="Issue a refund for any captured payment on Shopify." />
          <CheckRow checked={restock} onChange={setRestock} label="Restock items on Shopify" hint="Return the order's quantities to Shopify inventory." />
          <CheckRow checked={notifyCustomer} onChange={setNotifyCustomer} label="Notify the customer" hint="Send Shopify's cancellation email to the customer." />
        </div>
      </div>
    </Modal>
  );
}

function CheckRow({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-xs text-[var(--text-muted)]">{hint}</span>
      </span>
    </label>
  );
}

/** Search the ERP item master and map the selected item to a Shopify SKU. */
function MapSkuModal({ sku, onClose, onPick, pending }: { sku: string | null; onClose: () => void; onPick: (itemId: number) => void; pending: boolean }) {
  const [q, setQ] = useState('');
  const search = useQuery({
    queryKey: ['item-search', q],
    queryFn: () => listItems({ search: q, status: 'Active', pageSize: 20 }),
    enabled: sku != null && q.trim().length >= 2,
  });
  const items = search.data?.data ?? [];

  return (
    <Modal open={sku != null} onClose={onClose} size="md" title={<span className="flex items-center gap-2"><Link2 size={16} /> Map SKU “{sku}” to an ERP item</span>}>
      <div className="space-y-3">
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item by code, name or barcode (min 2 chars)" />
        <div className="max-h-[320px] overflow-y-auto rounded-card border border-[var(--border-color)]">
          {q.trim().length < 2 ? (
            <div className="py-8 text-center text-xs text-[var(--text-muted)]">Type at least 2 characters to search.</div>
          ) : search.isFetching ? (
            <div className="py-8 text-center text-xs text-[var(--text-muted)]">Searching…</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-xs text-[var(--text-muted)]">No items found.</div>
          ) : (
            items.map((it) => (
              <button key={it.id} type="button" disabled={pending} onClick={() => onPick(it.id)}
                className="flex w-full items-center justify-between gap-3 border-b border-[var(--border-color)] px-3 py-2 text-left last:border-b-0 hover:bg-[var(--bg-secondary)] disabled:opacity-50">
                <div>
                  <div className="text-sm font-semibold">{it.code}</div>
                  <div className="text-xs text-[var(--text-muted)]">{it.name}</div>
                </div>
                <div className="text-right text-xs text-[var(--text-muted)]">
                  {it.barcode && <div>{it.barcode}</div>}
                  <div>Stock: {it.warehouseStock?.currentQty ?? '0'}</div>
                </div>
              </button>
            ))
          )}
        </div>
        <p className="text-[11px] text-[var(--text-muted)]">This mapping is saved for the SKU and applied to all current and future orders carrying it.</p>
      </div>
    </Modal>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

/** "Silvora_180x270cm_D6 (need 1, 0 available)" — what an online order is waiting on. */
function shortText(list: StockShort[] | undefined): string {
  return (list ?? []).map((s) => `${s.code} (need ${s.need}, ${Math.max(0, s.available)} available)`).join(', ');
}
